import type { ItemCreate, ItemLite } from "@plannello/shared";
import { useEffect, useRef } from "react";

/**
 * The item view the keyboard acts on. Lists, boards, the detail page and the peek panel register
 * themselves; property shortcuts (S, P, A, L, T, M, 1-4) and the palette's item commands ask
 * `getActiveView()` for their targets. A view containing the focused element wins; otherwise the
 * most recently registered one does.
 */
export interface ActiveView {
  /** Root element, used to decide which view the focus is in. */
  root: () => HTMLElement | null;
  /** Selected items, else the focused one, else nothing. */
  targets: () => ItemLite[];
  /** Element to anchor pickers to (the focused row or card, or a property button). */
  anchor: () => HTMLElement | null;
  /** Moves focus by `delta` rows (J/K). Lists and boards only. */
  move?: (delta: number) => void;
  open?: (item: ItemLite) => void;
  peek?: (item: ItemLite) => void;
  toggleSelect?: (item: ItemLite) => void;
  /** Fields quick create prefills from this view (product, parent, column status). */
  createDefaults?: () => Partial<ItemCreate>;
  /** Puts focus back after a picker or dialog closes. */
  restoreFocus?: () => void;
  /** Detail view: the item itself. */
  kind: "list" | "board" | "detail";
}

type Entry = { token: symbol; view: ActiveView };
let entries: Entry[] = [];

export function registerView(view: ActiveView): () => void {
  const token = Symbol("view");
  entries = [...entries, { token, view }];
  return () => {
    entries = entries.filter((e) => e.token !== token);
  };
}

export function getActiveView(): ActiveView | null {
  const el = document.activeElement;
  for (let i = entries.length - 1; i >= 0; i--) {
    const root = entries[i]!.view.root();
    if (root && el && root.contains(el)) return entries[i]!.view;
  }
  return entries[entries.length - 1]?.view ?? null;
}

/** All registered views (topmost last); quick create merges their defaults. */
export function getViews(): ActiveView[] {
  return entries.map((e) => e.view);
}

/** Registers a view for the lifetime of the component; `view` may change between renders. */
export function useRegisterView(view: ActiveView, enabled = true) {
  const ref = useRef(view);
  ref.current = view;
  useEffect(() => {
    if (!enabled) return;
    const v = ref.current;
    return registerView({
      kind: v.kind,
      root: () => ref.current.root(),
      targets: () => ref.current.targets(),
      anchor: () => ref.current.anchor(),
      move: (d) => ref.current.move?.(d),
      open: (i) => ref.current.open?.(i),
      peek: (i) => ref.current.peek?.(i),
      toggleSelect: (i) => ref.current.toggleSelect?.(i),
      createDefaults: () => ref.current.createDefaults?.() ?? {},
      restoreFocus: () => ref.current.restoreFocus?.(),
    });
  }, [enabled]);
}
